import { useQuery } from '@tanstack/react-query';
import type { NavItemDto, UserDto } from '@cpm/shared';
import {
  Activity,
  Archive,
  Blocks,
  BookOpen,
  GitBranch,
  FolderLock,
  Globe,
  Import,
  LayoutDashboard,
  Layers,
  LogOut,
  Mail,
  Menu,
  Moon,
  MoveRight,
  Network,
  Package,
  RefreshCw,
  ScrollText,
  Server,
  Settings,
  ShieldCheck,
  Sun,
  UserCog,
  Users,
  Webhook,
  type LucideIcon,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { api } from '../api/client.js';
import { useLogout } from '../api/auth.js';
import { ImpersonationBanner } from './ImpersonationBanner.js';
import { applyTheme, resolveTheme } from '../lib/theme.js';
import { NotificationsNavItem } from './notifications-nav.js';
import { cx } from './ui.js';

/** Maps the icon keys modules declare to actual lucide components. */
const ICONS: Record<string, LucideIcon> = {
  activity: Activity,
  archive: Archive,
  'layout-dashboard': LayoutDashboard,
  users: Users,
  package: Package,
  layers: Layers,
  server: Server,
  'refresh-cw': RefreshCw,
  'user-cog': UserCog,
  'shield-check': ShieldCheck,
  'scroll-text': ScrollText,
  settings: Settings,
  blocks: Blocks,
  import: Import,
  mail: Mail,
  globe: Globe,
  move: MoveRight,
  webhook: Webhook,
  folder: FolderLock,
  network: Network,
  'book-open': BookOpen,
  'git-branch': GitBranch,
};

function useNav() {
  return useQuery({
    queryKey: ['system', 'nav'],
    queryFn: () => api.get<NavItemDto[]>('/api/system/nav'),
    staleTime: 5 * 60_000,
  });
}

export function Layout({
  user,
  panelName,
  panelVersion,
  children,
}: {
  user: UserDto;
  panelName: string;
  panelVersion: string;
  children: ReactNode;
}) {
  const nav = useNav();
  const logout = useLogout();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [theme, setTheme] = useState(resolveTheme);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    applyTheme(next);
    setTheme(next);
  };

  const navItems = nav.data ?? [];

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center gap-2.5 px-5">
        <div className="flex size-8 items-center justify-center rounded-lg bg-brand-600 text-sm font-bold text-white">
          CP
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{panelName}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">Version {panelVersion}</p>
        </div>
      </div>
      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-2">
        {navItems.map((item) => {
          const Icon = ICONS[item.icon] ?? Package;
          return (
            <NavLink
              key={`${item.moduleKey}:${item.path}`}
              to={item.path}
              end={item.path === '/'}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                cx(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white',
                )
              }
            >
              <Icon className="size-4.5 shrink-0" aria-hidden />
              {item.label}
            </NavLink>
          );
        })}
      </nav>
      {/*
        Shell chrome, not module nav: notifications is a core module (never
        disabled) and its entry needs a live unread badge the generic nav
        renderer cannot express.
      */}
      <div className="border-t border-slate-200 px-3 py-2 dark:border-slate-800">
        <NotificationsNavItem onNavigate={() => setMobileOpen(false)} />
      </div>
      <div className="border-t border-slate-200 p-3 dark:border-slate-800">
        <div className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{user.name}</p>
            <p className="truncate text-xs text-slate-500 capitalize dark:text-slate-400">{user.role}</p>
          </div>
          <div className="flex items-center">
            <button
              onClick={toggleTheme}
              className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
              aria-label="Toggle theme"
              title="Toggle theme"
            >
              {theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
            </button>
            <button
              onClick={() => logout.mutate()}
              className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-red-600 dark:text-slate-400 dark:hover:bg-slate-800"
              aria-label="Sign out"
              title="Sign out"
            >
              <LogOut className="size-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-full">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 border-r border-slate-200 bg-white lg:block dark:border-slate-800 dark:bg-slate-900">
        {sidebar}
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-slate-950/50" onClick={() => setMobileOpen(false)} aria-hidden />
          <aside className="fixed inset-y-0 left-0 w-64 bg-white shadow-xl dark:bg-slate-900">{sidebar}</aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile top bar */}
        <header className="flex h-14 items-center gap-3 border-b border-slate-200 bg-white px-4 lg:hidden dark:border-slate-800 dark:bg-slate-900">
          <button
            onClick={() => setMobileOpen(true)}
            className="rounded-md p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label="Open menu"
          >
            <Menu className="size-5" />
          </button>
          <span className="truncate text-sm font-semibold">{panelName}</span>
          <span className="ml-auto shrink-0 text-xs text-slate-500 dark:text-slate-400">
            v{panelVersion}
          </span>
        </header>

        <main className="flex-1 overflow-y-auto">
          <ImpersonationBanner />
          <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">{children}</div>
        </main>
      </div>
    </div>
  );
}
